// starCoinsClient.js - With improved checkout and verification
import { getFirestore, collection, getDocs, query, orderBy, limit as firestoreLimit, doc, getDoc, setDoc, addDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { trackStarCoinPurchase } from '../utils/pixels';

class StarCoinsClient {
  constructor() {
    this.auth = getAuth();
    this.db = getFirestore();
  }

  // Get the current user ID
  getUserId() {
    return this.auth.currentUser?.uid;
  }

  // Get available packages (with pro pricing if applicable)
  async getPackages(isPro = false) {
    try {
      const packagesRef = collection(this.db, 'starCoinsProducts');
      const packagesSnap = await getDocs(packagesRef);
      
      // Format packages with proper pricing
      return packagesSnap.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          amount: isPro && data.proAmount ? data.proAmount : data.amount,
          price: data.price,
          // Custom currency prices from Firestore
          brlPrice: data.brlPrice || null,
          mxnPrice: data.mxnPrice || null,
          arsPrice: data.arsPrice || null,
          copPrice: data.copPrice || null,
          clpPrice: data.clpPrice || null,
          // Pro bonus info
          proBonus: isPro && data.proAmount ? true : false,
          originalAmount: isPro && data.proAmount ? data.amount : null,
          popular: data.popular || false
        };
      }).sort((a, b) => a.price - b.price);
    } catch (error) {
      console.error('Error fetching packages:', error);
      return [];
    }
  }

  // Get transaction history
  async getTransactionHistory(limit = 20) {
    const userId = this.getUserId();
    if (!userId) return [];
    
    try {
      const transactionsRef = collection(this.db, 'users', userId, 'starCoinsTransactions');
      const q = query(transactionsRef, orderBy('timestamp', 'desc'), firestoreLimit(limit));
      const transactionsSnap = await getDocs(q);
      
      return transactionsSnap.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Handle both server timestamp and regular Date objects
          timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : new Date(data.timestamp)
        };
      });
    } catch (error) {
      console.error('Error fetching transactions:', error);
      return [];
    }
  }

  // Create a checkout session for Star Coins purchase
  // async createCheckout(packageId, amount, price, isPro = false) {
  //   const userId = this.getUserId();
  //   if (!userId) throw new Error('User not authenticated');
    
  //   try {
  //     console.log('Creating Star Coins checkout session through Firestore...');
      
  //     // Create a checkout session document
  //     const checkoutRef = collection(this.db, 'customers', userId, 'checkout_sessions');
      
  //     // Add client_reference_id to ensure we can identify the user
  //     const checkoutDoc = await addDoc(checkoutRef, {
  //       line_items: [
  //         {
  //           quantity: 1,
  //           price_data: {
  //             currency: 'usd',
  //             unit_amount: Math.round(price * 100),
  //             product_data: {
  //               name: `${amount} Star Coins`
  //             }
  //           }
  //         }
  //       ],
  //       mode: 'payment',
  //       success_url: window.location.origin + '?session_id={CHECKOUT_SESSION_ID}',
  //       cancel_url: window.location.origin,
  //       metadata: {
  //         type: 'star_coins',
  //         packageId: packageId,
  //         amount: amount.toString(),
  //         isPro: isPro ? 'true' : 'false',
  //         userId: userId
  //       },
  //       client_reference_id: userId
  //     });
      
  //     console.log('Created checkout document:', checkoutDoc.id);
      
  //     // Save the session ID in localStorage to check status after redirect
  //     localStorage.setItem('pendingStarCoinsSession', checkoutDoc.id);
      
  //     // Poll for the URL
  //     let attempts = 0;
  //     const maxAttempts = 50;
      
  //     while (attempts < maxAttempts) {
  //       attempts++;
        
  //       // Get the document
  //       const docSnap = await getDoc(doc(checkoutRef, checkoutDoc.id));
  //       const checkoutData = docSnap.data();
        
  //       console.log('Polling attempt', attempts, checkoutData);
        
  //       if (checkoutData?.url) {
  //         // Success! Redirect to the checkout URL
  //         console.log('Got checkout URL:', checkoutData.url);
  //         window.location.href = checkoutData.url;
  //         return true;
  //       }
        
  //       if (checkoutData?.error) {
  //         throw new Error(checkoutData.error.message || 'Error creating checkout');
  //       }
        
  //       // Wait 200ms before polling again
  //       await new Promise(resolve => setTimeout(resolve, 200));
  //     }
      
  //     throw new Error('Checkout creation timed out after multiple attempts');
  //   } catch (error) {
  //     console.error('Error creating checkout:', error);
  //     throw error;
  //   }
  // }

  // Create a checkout session for Star Coins purchase
// Create a checkout session for Star Coins purchase
async createCheckout(packageId, amount, price, isPro = false, currency = 'usd') {
  const userId = this.getUserId();
  if (!userId) throw new Error('User not authenticated');
  
  try {
    console.log('Creating Star Coins checkout session through Firestore...', { currency, price });
    
    // Generate a deterministic ID to prevent duplicates on double-click
    const timestamp = Math.floor(Date.now() / 10000); // 10-second window
    const sessionId = `${packageId}_${amount}_${timestamp}`;
    
    // Use setDoc with merge to make idempotent
    const checkoutRef = doc(this.db, 'customers', userId, 'checkout_sessions', sessionId);
    
    // Add client_reference_id to ensure we can identify the user
    await setDoc(checkoutRef, {
      line_items: [
        {
          quantity: 1,
          price_data: {
            currency: currency.toLowerCase(), // ONLY CHANGE: Use passed currency
            unit_amount: Math.round(price * 100),
            product_data: {
              name: `${amount} Star Coins`
            }
          }
        }
      ],
      mode: 'payment',
      success_url: window.location.origin + '?session_id={CHECKOUT_SESSION_ID}',
      cancel_url: window.location.origin,
      metadata: {
        type: 'star_coins',
        packageId: packageId,
        amount: amount.toString(),
        currency: currency, // ONLY CHANGE: Add currency to metadata
        isPro: isPro ? 'true' : 'false',
        userId: userId
      },
      client_reference_id: userId
    });
    
    console.log('Created checkout document:', sessionId);
    
    // Save the session ID in localStorage to check status after redirect
    localStorage.setItem('pendingStarCoinsSession', sessionId);
    
    // Poll for the URL
    let attempts = 0;
    const maxAttempts = 50;
    
    while (attempts < maxAttempts) {
      attempts++;
      
      // Get the document
      const docSnap = await getDoc(checkoutRef);
      const checkoutData = docSnap.data();
      
      console.log('Polling attempt', attempts, checkoutData);
      
      if (checkoutData?.url) {
        // Success! Redirect to the checkout URL
        console.log('Got checkout URL:', checkoutData.url);
        window.location.href = checkoutData.url;
        return true;
      }
      
      if (checkoutData?.error) {
        throw new Error(checkoutData.error.message || 'Error creating checkout');
      }
      
      // Wait 200ms before polling again
      await new Promise(resolve => setTimeout(resolve, 200));
    }
    
    throw new Error('Checkout creation timed out after multiple attempts');
  } catch (error) {
    console.error('Error creating checkout:', error);
    throw error;
  }
}
  
  // Consume Star Coins for a feature
  async consumeStarCoins(amount, feature) {
    const userId = this.getUserId();
    if (!userId) return { success: false, error: 'User not authenticated' };
    
    if (!amount || amount <= 0) {
      return { success: false, error: 'Invalid amount' };
    }
    
    if (!feature) {
      return { success: false, error: 'Feature must be specified' };
    }
    console.log(`[starCoinsClient] Consuming ${amount} coins for ${feature}`);

    try {
      // Create a consumption request document
      const consumptionRef = collection(this.db, 'starCoinsConsumption');
      const consumptionDoc = await addDoc(consumptionRef, {
        userId,
        amount,
        feature,
        timestamp: new Date(),
        status: 'pending'
      });
      
      console.log('Created consumption request:', consumptionDoc.id);
      
      // Poll for the result
      let attempts = 0;
      const maxAttempts = 25;
      
      while (attempts < maxAttempts) {
        attempts++;
        
        // Get the document
        const docSnap = await getDoc(doc(consumptionRef, consumptionDoc.id));
        const consumptionData = docSnap.data();
        
        if (consumptionData?.status === 'completed') {
          // Success!
          return { 
            success: true, 
            newBalance: consumptionData.newBalance 
          };
        }
        
        if (consumptionData?.status === 'failed') {
          return { 
            success: false, 
            error: consumptionData.error || 'Failed to consume Star Coins' 
          };
        }
        
        // Wait 200ms before polling again
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      
      return { success: false, error: 'Consumption request timed out' };
    } catch (error) {
      console.error('Error consuming Star Coins:', error);
      return { success: false, error: error.message };
    }
  }

  // Add Star Coins back (refund) for failed predictions
  async addStarCoins(amount, reason = 'refund') {
    const userId = this.getUserId();
    if (!userId) return { success: false, error: 'User not authenticated' };
    
    if (!amount || amount <= 0) {
      return { success: false, error: 'Invalid amount' };
    }
    
    console.log(`[starCoinsClient] Refunding ${amount} coins for ${reason}`);

    try {
      // Create a refund request document (negative consumption)
      const consumptionRef = collection(this.db, 'starCoinsConsumption');
      const refundDoc = await addDoc(consumptionRef, {
        userId,
        amount: -amount, // Negative amount for refund
        feature: reason,
        timestamp: new Date(),
        status: 'pending',
        isRefund: true
      });
      
      console.log('Created refund request:', refundDoc.id);
      
      // Poll for the result
      let attempts = 0;
      const maxAttempts = 25;
      
      while (attempts < maxAttempts) {
        attempts++;
        
        // Get the document
        const docSnap = await getDoc(doc(consumptionRef, refundDoc.id));
        const refundData = docSnap.data();
        
        if (refundData?.status === 'completed') {
          // Success!
          return { 
            success: true, 
            newBalance: refundData.newBalance 
          };
        }
        
        if (refundData?.status === 'failed') {
          return { 
            success: false, 
            error: refundData.error || 'Failed to refund Star Coins' 
          };
        }
        
        // Wait 200ms before polling again
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      
      return { success: false, error: 'Refund request timed out' };
    } catch (error) {
      console.error('Error refunding Star Coins:', error);
      return { success: false, error: error.message };
    }
  }

  async verifyAndProcessPurchase() {
    // Check if there's a session ID in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (!sessionId) return false;
    
    try {
      console.log('Verifying purchase from URL:', sessionId);
      
      // First, try to verify through our Cloud Function
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const verifyFunction = httpsCallable(functions, 'verifyStarCoinsPurchase');
      
      const result = await verifyFunction({ sessionId });
      console.log('Verification result:', result.data);
      
      if (result.data.success) {
        console.log(`Purchase verified! Status: ${result.data.status}, Amount: ${result.data.amount} Star Coins`);
        
        // Track successful purchase with Facebook Pixel
        const userId = this.getUserId();
        
        // Determine price - use actual price from session or fall back to standard pricing
        const standardPricing = {
          12: 0.99,
          60: 4.99,
          125: 9.99,
          250: 19.99,
          650: 49.99,
          1350: 99.99
        };
        
        const price = result.data.price || standardPricing[result.data.amount] || 0.99;
        const packageId = result.data.packageId || `star_coins_${result.data.amount}`;
        
        // Track the purchase with Facebook
        if (result.data.amount && userId) {
          console.log('Tracking Star Coins purchase with Facebook:', { packageId, price, amount: result.data.amount });
          trackStarCoinPurchase(
            packageId,
            price,
            result.data.amount,
            userId
          );
        }
        
        // Log the status for debugging
        if (result.data.status === 'already_processed') {
          console.log('Purchase was already processed and credited');
        } else if (result.data.status === 'pending') {
          console.log('Purchase verified, coins will be credited shortly by backend');
        }
        
        return true;
      }
      
      // If the verification shows it's still pending, check the status in Stripe
      if (result.data.status === 'pending') {
        console.log('Purchase still pending, will check again in 2 seconds...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        return this.verifyAndProcessPurchase();
      }
      
      console.log('Purchase verification failed');
      return false;
    } catch (error) {
      console.error('Error verifying purchase:', error);
      return false;
    } finally {
      // Clear the session ID from the URL to prevent duplicate processing
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }

  // Check if a user can use Star Coins for a feature
  async canUseStarCoinsForFeature(userId, feature) {
    if (!userId) {
      userId = this.getUserId();
    }
    
    if (!userId) return { canUse: false };
    
    try {
      // Get the user's current balance
      const userRef = doc(this.db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) return { canUse: false };
      
      const userData = userDoc.data();
      const balance = userData.starCoins?.balance || 0;
      
      // Get the cost for this feature
      const costs = {
        customPromptCreation: 33,  // Updated from 10 to 33
        customPromptUse: 11,       // Updated from 5 to 11
        aiMessage: 11,             // Updated from 5 to 11
        aiSlotSpin: 7,             // Cost for predictions and slot spins
        premiumPrompt: 15          // Keep as-is
      };
      
      const cost = costs[feature] || 0;
      
      return {
        canUse: balance >= cost,
        cost,
        balance
      };
    } catch (error) {
      console.error('Error checking if can use Star Coins:', error);
      return { canUse: false };
    }
  }
  
  // Load current balance
  async loadBalance() {
    const userId = this.getUserId();
    if (!userId) return 0;
    
    try {
      const userRef = doc(this.db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.starCoins?.balance || 0;
      }
      
      return 0;
    } catch (error) {
      console.error('Error loading Star Coins balance:', error);
      return 0;
    }
  }
}

export const starCoinsClient = new StarCoinsClient();


